<template>
  <nav
    class="navbar navbar-main navbar-expand-lg px-0 mx-2 mx-md-4 shadow-none border-radius-xl position-sticky custom-navbar left-auto top-1 z-index-sticky"
    v-bind="$attrs"
    id="navbarBlur"
  >
    <div class="container-fluid py-1 px-2 px-md-3">

      <!-- MOBILE TOGGLE BUTTON (Hamburger icon) -->
      <div class="sidenav-toggler sidenav-toggler-inner d-xl-none me-3" @click="navbarMinimize" style="cursor: pointer;">
        <a href="javascript:;" class="nav-link text-white p-0">
          <div class="sidenav-toggler-inner">
            <i class="sidenav-toggler-line bg-white" style="display: block; width: 18px; height: 2px; margin-bottom: 3px; background: white;"></i>
            <i class="sidenav-toggler-line bg-white" style="display: block; width: 18px; height: 2px; margin-bottom: 3px; background: white;"></i>
            <i class="sidenav-toggler-line bg-white" style="display: block; width: 18px; height: 2px; background: white;"></i>
          </div>
        </a>
      </div>

      <!-- Breadcrumbs - Hidden on very small screens to save space -->
      <div class="d-none d-sm-flex align-items-center">
        <breadcrumbs :currentPage="currentRouteName" :textWhite="true" />
        <span v-if="displayTenantName" class="badge text-white text-xs px-2 py-1 ms-2 font-weight-bold shadow-none" style="background-color: rgba(255, 255, 255, 0.2); border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px;">
          <i class="fas fa-hospital me-1"></i> {{ displayTenantName }}
        </span>
      </div>

      <!-- Mobile Toggle for Breadcrumbs/Title if needed -->
      <div class="d-block d-sm-none">
         <h6 class="text-white font-weight-bolder mb-0">{{ displayTenantName ? `${displayTenantName} - ${currentRouteName}` : currentRouteName }}</h6>
      </div>

      <div class="collapse navbar-collapse mt-sm-0 mt-2 me-md-0 me-sm-4" id="navbar">

        <!-- Search Bar - Responsive width -->
        <div class="ms-md-auto pe-md-3 d-flex align-items-center position-relative w-100 w-md-auto my-2 my-md-0" id="search-area">
          <div class="input-group search-container shadow-none w-100" :class="{ 'search-focused': isSearchFocused }">
            <span class="input-group-text text-body border-end-0 bg-transparent ps-3">
              <i class="fas fa-search" :style="{ color: isSearchFocused ? activeTheme : 'rgba(255,255,255,0.55)' }" aria-hidden="true"></i>
            </span>
            <input
              type="text"
              class="form-control border-start-0 ps-1 bg-transparent text-sm text-white custom-input-no-focus"
              placeholder="Search modules..."
              v-model="searchQuery"
              @focus="onSearchFocus"
              @blur="onSearchBlur"
              @keyup.enter="handleEnterSearch"
            />
          </div>

          <ul v-if="showSuggestions && filteredRoutes.length > 0" class="custom-dropdown-menu search-results shadow-lg border-0">
            <li
              v-for="route in filteredRoutes"
              :key="route.name"
              @click="navigateTo(route.name)"
              class="dropdown-item-custom"
            >
              <i class="fas fa-terminal text-xxs me-2 opacity-6" :style="{ color: activeTheme }"></i>
              {{ route.label }}
            </li>
          </ul>
        </div>

        <ul class="navbar-nav justify-content-end align-items-center flex-row">

          <!-- Doctor Availability Status Selector -->
          <li v-if="isDoctor" class="nav-item d-flex align-items-center me-3">
            <div class="doctor-status-badge">
              <select 
                v-model="doctorStatus" 
                @change="changeDoctorStatus" 
                :class="statusClass"
                class="form-select form-select-sm status-dropdown shadow-none"
              >
                <option value="Available">🟢 Available</option>
                <option value="Busy">🔴 Busy / In Consultation</option>
                <option value="On Break">🟡 On Break</option>
                <option value="Offline">⚫ Offline</option>
              </select>
            </div>
          </li>

          <!-- Tenant Cloud Storage Widget -->
          <li v-if="tenantStorage" class="nav-item d-flex align-items-center me-3" style="cursor: default;">
            <div class="d-flex flex-column" style="min-width: 145px;" :title="`Used: ${tenantStorage.total_used_mb} MB of ${tenantStorage.quota_mb} MB`">
              <div class="d-flex justify-content-between align-items-center mb-1 text-xxs font-weight-bold">
                <span style="color: rgba(255, 255, 255, 0.85) !important;">
                  <i class="fas fa-database me-1 text-warning"></i> Storage
                </span>
                <span :style="{ color: tenantStorage.percentage_used > 85 ? '#ff4d4f' : '#ffffff' }" style="font-weight: 700 !important; color: #ffffff !important;">
                  {{ tenantStorage.total_used_gb || '0.00' }} / {{ ((tenantStorage.quota_mb || 5120) / 1024).toFixed(0) }} GB
                </span>
              </div>
              <div class="progress" style="height: 6px; border-radius: 4px; background-color: rgba(255, 255, 255, 0.25) !important; overflow: hidden;">
                <div 
                  class="progress-bar transition-all" 
                  role="progressbar" 
                  :style="{ 
                    width: Math.min(100, Math.max(5, Number(tenantStorage.percentage_used || 0))) + '%', 
                    backgroundColor: tenantStorage.percentage_used >= 90 ? '#f5365c' : tenantStorage.percentage_used >= 75 ? '#fb6340' : '#2dce89' 
                  }"
                  :aria-valuenow="tenantStorage.percentage_used" 
                  aria-valuemin="0" 
                  aria-valuemax="100">
                </div>
              </div>
            </div>
          </li>

          <!-- LANGUAGE SELECTOR -->
          <li class="nav-item pe-3 d-flex align-items-center position-relative" id="lang-area">
            <div
              class="nav-link p-0 text-white font-weight-bold text-xs text-uppercase cursor-pointer"
              @click="showLangDropdown = !showLangDropdown"
            >
              <i class="fas fa-globe-americas me-1"></i>
              <span class="d-none d-sm-inline">{{ $i18n.locale }}</span>
              <i class="fa fa-chevron-down text-xxs ms-1"></i>
            </div>

            <ul v-if="showLangDropdown" class="custom-dropdown-menu shadow-sm border-0">
              <li @click="changeLanguage('en')" class="dropdown-item-custom">English (EN)</li>
              <li @click="changeLanguage('hi')" class="dropdown-item-custom">हिन्दी (HI)</li>
              <li @click="changeLanguage('mr')" class="dropdown-item-custom">मराठी (MR)</li>
            </ul>
          </li>

          <!-- USER ACCOUNT PILL -->
          <li class="nav-item d-flex align-items-center position-relative" id="user-area">
            <div
              class="user-pill d-flex align-items-center cursor-pointer"
              @click="showUserDropdown = !showUserDropdown"
            >
              <div class="user-avatar-small" :style="{ backgroundColor: activeTheme }">
                <i class="fa fa-user-shield text-white text-xxs"></i>
              </div>
              <i class="fa fa-chevron-down text-xxs text-white ms-2"></i>
            </div>

            <ul v-if="showUserDropdown" class="custom-dropdown-menu user-dropdown shadow-lg border-0">

              <!-- Profile Header -->
              <li class="px-3 py-3 rounded-top profile-header-dynamic">
                <div class="d-flex flex-column">
                  <span class="text-sm font-weight-bolder" style="color:#ffffff !important">{{ adminName }}</span>
                  <span class="text-xs" style="color:rgba(255,255,255,0.7) !important">{{ adminEmail }}</span>
                   <span class="text-xxs font-weight-bold text-uppercase mt-1" 
          style="color: #ffffff !important; background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 4px; width: fit-content;">
      {{ adminRole }}
    </span>
                </div>
              </li>

              <!-- ADDED: Self Password Change Option with Confirmation -->
              <li class="px-2 py-1">
                <div @click="openSelfPasswordModal" class="dropdown-item-custom">
                  <i class="fa fa-key me-2 text-xs"></i> Change Password
                </div>
              </li>

              <!-- Theme Picker -->
              <li class="px-3 py-2 theme-section">
                <p class="theme-label">Choose Theme</p>
                <div class="d-flex flex-wrap gap-2 mt-1">
                  <div
                    v-for="c in themePresets"
                    :key="c"
                    @click="updateGlobalTheme(c)"
                    class="theme-swatch"
                    :style="{
                      backgroundColor: c,
                      border: activeTheme === c ? '3px solid #ffffff' : '2px solid rgba(255,255,255,0.3)'
                    }"
                  ></div>
                </div>
              </li>
              

              <!-- Logout -->
              <li class="px-2 py-1">
            <div @click="logout" class="logout-btn">
                  <i class="fa fa-power-off me-2"></i> LOG OUT
                </div>
              </li>

            </ul>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script>
import Breadcrumbs from "../Breadcrumbs.vue";
import { mapMutations, mapActions } from "vuex";
import axios from 'axios';
import Swal from 'sweetalert2';
import { io } from "socket.io-client";
import { hasPermission } from "../../utils/permissions";

export default {
  name: "navbar",
  components: { Breadcrumbs },
  props: ["textWhite"],
  data() {
    return {
      isSearchFocused: false,
      searchQuery: "",
      showSuggestions: false,
      showLangDropdown: false,
      showUserDropdown: false,
      adminName: localStorage.getItem('userName') || 'Admin',
      adminEmail: localStorage.getItem('adminEmail') || 'admin@bhoomida.com',
      adminRole: localStorage.getItem('userRole'),
      activeTheme: localStorage.getItem('themeColor') || '#1e3a5f',
      themePresets: [
        '#1e3a5f',
        '#0f766e',
        '#1f2937',
        '#164e63',
        '#312e5e',
        '#14532d',
      ],
      doctorStatus: localStorage.getItem('doctor_status') || 'Available',
      doctorStatusPoll: null,
      socket: null,
      tenantStorage: {
        total_used_mb: 0,
        total_used_gb: '0.00',
        quota_mb: 5120,
        percentage_used: 0
      },
      modules: [
        { label: "Dashboard",          name: "Dashboard" },
        { label: "Medical Dashboard",  name: "Medical Dashboard" },
        { label: "Services & Prefix",  name: "Services & Prefix" }, // ADDED NEW MODULE
        { label: "User Management",    name: "User Management" }, // ADDED NEW MODULE
        { label: "Content",            name: "Content" },
        { label: "Users",              name: "Users" },
        { label: "Media",              name: "Media" },
        { label: "Roles",              name: "Roles" },
        { label: "Device Group",       name: "Device Group" },
        { label: "User Group",         name: "User Group" },
        { label: "Play List",          name: "Play List" },
        { label: "Branches",           name: "Branches" },
        { label: "Devices",            name: "Devices" },
        { label: "Deleted Content",    name: "DeletedContent" },
        { label: "Emergency Broadcast",name: "Emergency Broadcast" },
        { label: "Device Position",    name: "Device Position" },
        { label: "Tokens",             name: "Tokens" },
        { label:"Counter" ,            name: "Counter" },
      ]
    };
  },
  computed: {
    currentRouteName() { return this.$route.name; },
    displayTenantName() {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u.company_name) return u.company_name;
        if (u.tenant_name) return u.tenant_name;
        if (u.hospital_name) return u.hospital_name;
        if (u.companyShortName) return u.companyShortName;
        if (u.name && !u.name.toLowerCase().includes('dr.') && !u.name.toLowerCase().includes('admin')) return u.name;
      } catch (e) {
        console.warn("Tenant name parse note:", e);
      }
      return localStorage.getItem('company_name') || localStorage.getItem('tenant_name') || '';
    },
    isDoctor() {
      const role = (localStorage.getItem('role') || localStorage.getItem('userRole') || '').toLowerCase();
      let isDoc = role.includes('doctor') || role.includes('medical') || role.includes('physician');
      if (!isDoc) {
        try {
          const u = JSON.parse(localStorage.getItem('user') || '{}');
          const uRole = String(u.role_name || u.role_slug || (u.role && (u.role.name || u.role.slug)) || u.role || '').toLowerCase();
          if (uRole.includes('doctor') || uRole.includes('medical') || uRole.includes('physician')) isDoc = true;
        } catch (e) {
          console.warn("User parse error in isDoctor:", e);
        }
      }
      return isDoc;
    },
    statusClass() {
      const s = (this.doctorStatus || '').toLowerCase();
      return {
        'status-available': s === 'available',
        'status-busy': s.includes('busy') || s.includes('consultation'),
        'status-break': s.includes('break'),
        'status-offline': s.includes('off')
      };
    },
    filteredRoutes() {
      if (!this.searchQuery) return [];
      return this.modules.filter(m =>
        m.label.toLowerCase().includes(this.searchQuery.toLowerCase()) && 
        this.hasAccess(m.name) // Applied Role Based Access check here
      );
    }
  },
  methods: {
    ...mapMutations(["navbarMinimize"]),
    ...mapActions(["toggleSidebarColor"]),

    // UPDATED: Dynamic Role & Module Based Access control method
    hasAccess(moduleName) {
      return hasPermission(moduleName, 'read');
    },

    // UPDATED: Self password modal with ID validation and enhanced error reporting
    openSelfPasswordModal() {
      const myId = localStorage.getItem('id');
      const token = localStorage.getItem('token');
      const currentEmail = localStorage.getItem('adminEmail') || '';

      // GUARD: Prevent request if session is corrupted or ID is null
      if (!myId || myId === 'null') {
        Swal.fire('Session Error', 'Your User ID is missing. Please log out and log in again to fix this.', 'error');
        return;
      }

      Swal.fire({
        title: 'Update Password',
        html: `
          <div style="margin-bottom: 15px; width: 100%;">
            <label style="display: block; text-align: left; font-size: 12px; font-weight: bold; margin-left: 10%; color: #888;">Email Address (Read-only)</label>
            <input type="text" value="${currentEmail}" class="swal2-input" style="width: 80%; background-color: #f8f9fa; cursor: not-allowed;" readonly>
          </div>
          <div style="position: relative; width: 100%;">
            <input type="password" id="new-password" class="swal2-input" placeholder="New Password" style="width: 80%;">
            <i class="fas fa-eye" id="toggle-new" style="position: absolute; right: 45px; top: 30px; cursor: pointer; color: #666;"></i>
          </div>
          <div style="position: relative; width: 100%;">
            <input type="password" id="confirm-password" class="swal2-input" placeholder="Confirm Password" style="width: 80%;">
            <i class="fas fa-eye" id="toggle-confirm" style="position: absolute; right: 45px; top: 30px; cursor: pointer; color: #666;"></i>
          </div>
        `,
        didOpen: () => {
          const setupToggle = (btnId, inputId) => {
            const btn = document.getElementById(btnId);
            const input = document.getElementById(inputId);
            btn.addEventListener('click', () => {
              const type = input.getAttribute('type') === 'password' ? 'text' : 'password';
              input.setAttribute('type', type);
              btn.classList.toggle('fa-eye');
              btn.classList.toggle('fa-eye-slash');
            });
          };
          setupToggle('toggle-new', 'new-password');
          setupToggle('toggle-confirm', 'confirm-password');
        },
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Update',
        confirmButtonColor: this.activeTheme,
        preConfirm: () => {
          const pass = document.getElementById('new-password').value;
          const confirm = document.getElementById('confirm-password').value;
          if (!pass || !confirm) {
            Swal.showValidationMessage('Please fill both fields');
            return false;
          }
          if (pass.length < 6) {
            Swal.showValidationMessage('Password must be at least 6 characters');
            return false;
          }
          if (pass !== confirm) {
            Swal.showValidationMessage('Passwords do not match');
            return false;
          }
          return pass;
        }
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;

          axios.post(`${apiBaseUrl}/v1/admin/user/change-password/${myId}`, 
            { password: result.value },
            { headers: { Authorization: `Bearer ${token}` } }
          ).then(() => {
            Swal.fire('Success', 'Your password has been updated.', 'success');
          }).catch(err => {
            // Enhanced error message for signature/null issues
            const msg = err.response?.data?.message || err.response?.data?.error || 'Update failed';
            Swal.fire('Error', msg, 'error');
          });
        }
      });
    },

    // UPDATED: Added is_media_user check to match updated AdminAuthController
async updateGlobalTheme(color) {
  this.activeTheme = color;
  localStorage.setItem('themeColor', color);
  this.applyThemeStyles(color);
  
  try {
    const token = localStorage.getItem('token'); // Use whatever key you used at login
    const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
    
    if (token) {
      // We use axios (the imported one) but MANUALLY pass the header 
      // This bypasses any issues with the global interceptor
      await axios.post(`${apiBaseUrl}/v1/admin/auth/update-theme`, 
        { theme_color: color }, 
        { 
          headers: { 
            Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` 
          } 
        }
      );
    }
  } catch (error) {
    console.error("Theme sync failed:", error);
    // If it still redirects here, the problem is definitely in your adminMiddleware.js
  }
},
    applyThemeStyles(color) {
      // UPDATED: Inject CSS variables into document root so all components (Tokens, Candidates) can access them
      const root = document.documentElement;
      root.style.setProperty('--bs-primary', color);
      root.style.setProperty('--bs-primary-rgb', color); 
      root.style.setProperty('--theme-primary', color);
      root.style.setProperty('--bs-success', color);

      let styleTag = document.getElementById('dynamic-theme-css');
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = 'dynamic-theme-css';
        document.head.appendChild(styleTag);
      }

      styleTag.innerHTML = `

        /* ── NAVBAR ── */
        .custom-navbar {
          background-color: ${color} !important;
          background-image: none !important;
        }

        /* ── SIDEBAR BACKGROUND ── */
        #sidenav-main,
        .sidenav,
        .navbar-vertical,
        aside.sidenav {
          background-color: ${color} !important;
          background-image: none !important;
        }

        /* ── SIDEBAR LOGO / BRAND ── */
        #sidenav-main .sidenav-header,
        #sidenav-main .sidenav-header *,
        #sidenav-main .navbar-brand,
        #sidenav-main .navbar-brand * {
          color: #ffffff !important;
          opacity: 1 !important;
          visibility: visible !important;
        }

        /* logo image — invert dark logo to white */
        #sidenav-main .navbar-brand img,
        #sidenav-main .sidenav-header img {
          filter: brightness(0) invert(1) !important;
          opacity: 1 !important;
        }

        /* ── SIDEBAR ALL NAV TEXT + ICONS (inactive) ── */
        #sidenav-main .nav-item .nav-link,
        #sidenav-main .nav-item .nav-link .nav-link-text,
        #sidenav-main .nav-item .nav-link span,
        #sidenav-main .nav-item .nav-link p,
        #sidenav-main .nav-item .nav-link i,
        #sidenav-main .nav-item .nav-link .icon-shape i,
        .sidenav .nav-item .nav-link,
        .sidenav .nav-item .nav-link * {
          color: rgba(255, 255, 255, 0.82) !important;
          opacity: 1 !important;
          visibility: visible !important;
        }

        /* ── SIDEBAR ICON SHAPE (inactive) ── */
        #sidenav-main .nav-item .nav-link .icon-shape,
        .sidenav .nav-item .nav-link .icon-shape {
          background-color: rgba(255, 255, 255, 0.15) !important;
          background-image: none !important;
          width: 32px !important;
          height: 32px !important;
          border-radius: 8px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          opacity: 1 !important;
          visibility: visible !important;
        }

        /* svg icons inside icon-shape */
        #sidenav-main .nav-item .nav-link .icon-shape svg,
        #sidenav-main .nav-item .nav-link .icon-shape svg path,
        #sidenav-main .nav-item .nav-link .icon-shape svg rect,
        #sidenav-main .nav-item .nav-link .icon-shape svg circle {
          fill: rgba(255,255,255,0.85) !important;
          stroke: none !important;
          opacity: 1 !important;
        }

        /* ── SIDEBAR NAV HOVER ── */
        #sidenav-main .nav-item .nav-link:hover,
        #sidenav-main .nav-item .nav-link:hover *,
        .sidenav .nav-item .nav-link:hover,
        .sidenav .nav-item .nav-link:hover * {
          color: #ffffff !important;
          opacity: 1 !important;
        }

        #sidenav-main .nav-item .nav-link:hover .icon-shape {
          background-color: rgba(255, 255, 255, 0.25) !important;
        }

        /* ── SIDEBAR ACTIVE ITEM ── */
        #sidenav-main .nav-item .nav-link.active,
        .sidenav .nav-item .nav-link.active {
          background-color: rgba(255, 255, 255, 0.15) !important;
          border-left: 3px solid #ffffff !important;
          border-radius: 0 8px 8px 0 !important;
        }

        #sidenav-main .nav-item .nav-link.active,
        #sidenav-main .nav-item .nav-link.active *,
        .sidenav .nav-item .nav-link.active,
        .sidenav .nav-item .nav-link.active * {
          color: #ffffff !important;
          font-weight: 600 !important;
          opacity: 1 !important;
          visibility: visible !important;
        }

        /* active icon shape — solid white box, colored icon */
        #sidenav-main .nav-item .nav-link.active .icon-shape {
          background-color: #ffffff !important;
          background-image: none !important;
        }

        #sidenav-main .nav-item .nav-link.active .icon-shape i {
          color: ${color} !important;
        }

        #sidenav-main .nav-item .nav-link.active .icon-shape svg path,
        #sidenav-main .nav-item .nav-link.active .icon-shape svg rect,
        #sidenav-main .nav-item .nav-link.active .icon-shape svg circle {
          fill: ${color} !important;
        }

        /* ── SIDEBAR DIVIDERS ── */
        #sidenav-main hr,
        .sidenav hr,
        .navbar-vertical hr {
          border-color: rgba(255, 255, 255, 0.18) !important;
          background-color: rgba(255, 255, 255, 0.18) !important;
          opacity: 1 !important;
        }

        /* ── SIDEBAR SCROLLBAR ── */
        #sidenav-main::-webkit-scrollbar { width: 4px; }
        #sidenav-main::-webkit-scrollbar-thumb {
          background-color: rgba(255,255,255,0.2) !important;
          border-radius: 4px;
        }

        /* ── DASHBOARD CARD ICON SHAPES ── */
        .card .icon-shape {
          background-color: ${color} !important;
          background-image: none !important;
        }

        .card .icon-shape i {
          color: #ffffff !important;
        }

        /* ── BUTTONS ── */
        .btn-success, .btn-primary, .btn-mint, .btn-violet,
        .bg-gradient-success, .bg-gradient-primary, .bg-gradient-info {
          background-color: ${color} !important;
          background-image: none !important;
          border-color: ${color} !important;
          color: #ffffff !important;
        }

        .btn-success:hover, .btn-primary:hover, .btn-mint:hover {
          filter: brightness(0.88);
        }

        /* ── PAGINATION ── */
        .pagination .page-item.active .page-link {
          background-color: ${color} !important;
          border-color: ${color} !important;
          color: #ffffff !important;
        }

        .pagination .page-link, .page-link {
          color: ${color} !important;
          box-shadow: none !important;
          border-radius: 8px !important;
          margin: 0 3px;
        }

        .pagination .page-link:hover {
          background-color: rgba(0,0,0,0.05) !important;
          color: ${color} !important;
        }

        .page-item.disabled .page-link {
          color: #67748e !important;
          opacity: 0.5;
        }

        /* ── FORM FOCUS ── */
        .form-control:focus, .form-select:focus {
          border-color: ${color} !important;
          box-shadow: 0 0 0 2px ${color}33 !important;
        }

        /* ── CHECKBOXES ── */
        .form-check-input:checked {
          background-color: ${color} !important;
          border-color: ${color} !important;
        }

        /* UPDATED: Targeted h6 to avoid white text in tables */
        .breadcrumb-item, .breadcrumb-item a, .breadcrumb-item i, .custom-navbar h6 { 
          color: #ffffff !important; 
          font-weight: 800 !important; 
          opacity: 1 !important; 
        }
        .breadcrumb-item + .breadcrumb-item::before { 
          color: #ffffff !important; 
        }

        /* ── FOOTER ── */
.footer {
  position: fixed;
  bottom: 1rem;
  left: calc(250px + 1.5rem) !important; 
  right: 1.5rem !important;
  background-color: ${color} !important;
  border-radius: 10px !important; 
  z-index: 1000;
  transition: all 0.3s ease;
  padding: 10px 0 !important; 
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  width: auto !important;
  border: none;
}

/* ── SINGLE LINE TEXT ── */
.footer .copyright {
  color: #ffffff !important;
  font-size: 0.75rem !important;
  font-weight: 500;
  margin: 0 !important;
  text-align: center !important;
  width: 100%;
  display: block;
  white-space: nowrap;
}

.footer .copyright a {
  color: #ffffff !important;
  text-decoration: none;
}

/* ── MOBILE FIX (The crucial part) ── */
@media (max-width: 1199.98px) {
  .footer {
    /* Change fixed to relative so it moves to the end of the list */
    position: relative !important; 
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    margin: 20px 15px !important; /* Add some spacing around it */
    width: calc(100% - 30px) !important;
  }
}

/* Adjust page content padding */
main.main-content {
  padding-bottom: 20px !important; 
}
      `;
    },

    onSearchFocus()  { this.isSearchFocused = true;  this.showSuggestions = true; },
    onSearchBlur()   { this.isSearchFocused = false; },
    changeLanguage(lang) { this.$i18n.locale = lang; this.showLangDropdown = false; },
    navigateTo(routeName) {
      this.$router.push({ name: routeName });
      this.searchQuery = "";
      this.showSuggestions = false;
      // Close sidebar after navigating on mobile
      if (window.innerWidth < 1200) {
        this.navbarMinimize();
      }
    },
    handleEnterSearch() {
      if (this.filteredRoutes.length > 0) this.navigateTo(this.filteredRoutes[0].name);
    },

    // UPDATED: Robust logout handling that clears session credentials while preserving hospital configuration & doctor availability
    logout() { 
      this.showUserDropdown = false;
      const preservedKeys = [
        'hospitalDoctorShifts', 
        'doctorShiftSession', 
        'doctorDepartment',
        'doctorRoom',
        'doctor_status',
        'themeColor',
        'installationType',
        'systemModules'
      ];
      const preservedData = {};
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && (preservedKeys.includes(k) || k.startsWith('doctorShift_') || k.startsWith('hospital_'))) {
            preservedData[k] = localStorage.getItem(k);
          }
        }
      } catch (e) {
        console.warn("Could not read preserved localStorage keys:", e);
      }

      localStorage.clear(); 
      sessionStorage.clear();

      try {
        Object.keys(preservedData).forEach(k => {
          localStorage.setItem(k, preservedData[k]);
        });
      } catch (e) {
        console.warn("Could not restore preserved localStorage keys:", e);
      }

      window.location.href = '/'; 
    },

    normalizeStatus(status) {
      if (!status) return 'Available';
      const s = String(status).trim().toLowerCase();
      if (s.includes('busy') || s.includes('consultation')) return 'Busy';
      if (s.includes('break')) return 'On Break';
      if (s.includes('off')) return 'Offline';
      return 'Available';
    },

    async fetchDoctorStatus() {
      /* eslint-disable require-atomic-updates */
      if (!this.isDoctor) return;
      try {
        const rawBase = (process.env.VUE_APP_API_BASE_URL || '/api').replace(/\/+$/, '');
        const baseUrl = rawBase.endsWith('/api') ? rawBase : `${rawBase}/api`;
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('admin_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const userId = localStorage.getItem('id');
        const userEmail = (localStorage.getItem('adminEmail') || localStorage.getItem('email') || '').toLowerCase().trim();
        const userName = (localStorage.getItem('userName') || '').toLowerCase().replace(/^dr\.?\s*/i, '').trim();

        let statusFound = null;

        // 1. Check user record from backend /v1/admin/user?_t=... (where Admin status updates are stored directly on user)
        if (token) {
          try {
            const uRes = await axios.get(`${baseUrl}/v1/admin/user?_t=${Date.now()}`, { headers });
            const users = Array.isArray(uRes.data) ? uRes.data : (uRes.data?.data || uRes.data?.users || []);
            if (Array.isArray(users) && users.length > 0) {
              const me = users.find(u => {
                if (userId && String(u.id) === String(userId)) return true;
                if (userEmail && u.email && u.email.toLowerCase().trim() === userEmail) return true;
                if (userName && (u.name || u.f_name)) {
                  const n = String(u.name || `${u.f_name || ''} ${u.l_name || ''}`).toLowerCase().replace(/^dr\.?\s*/i, '').trim();
                  if (n === userName || n.includes(userName) || userName.includes(n)) return true;
                }
                return false;
              });
              if (me && (me.availability_status || me.doctor_status)) {
                statusFound = me.availability_status || me.doctor_status;
              }
            }
          } catch (uErr) {
            /* ignore */
          }
        }

        // 2. Check local hospitalDoctorShifts registry (synced instantly across tabs)
        if (!statusFound) {
          try {
            const allShifts = JSON.parse(localStorage.getItem('hospitalDoctorShifts') || '{}');
            const saved = (userId && allShifts[String(userId)]) ||
              (userEmail && allShifts[userEmail]) ||
              (userName && allShifts[userName]) ||
              Object.values(allShifts).find(s => 
                (userId && String(s.doctor_id || s.id || s.user_id) === String(userId)) ||
                (userEmail && s.doctor_email && s.doctor_email.toLowerCase().trim() === userEmail) ||
                (userName && s.doctor_name && s.doctor_name.toLowerCase().replace(/^dr\.?\s*/i, '').trim().includes(userName))
              );
            if (saved && (saved.availability_status || saved.status)) {
              statusFound = saved.availability_status || saved.status;
            }
          } catch (sErr) {
            /* ignore */
          }
        }

        // 3. Check personal doctorShift_${userId}
        if (!statusFound && userId) {
          try {
            const s = JSON.parse(localStorage.getItem(`doctorShift_${userId}`) || 'null');
            if (s && (s.availability_status || s.status)) {
              statusFound = s.availability_status || s.status;
            }
          } catch (e) {
            /* ignore */
          }
        }

        // 4. Check doctorShiftSession
        if (!statusFound) {
          try {
            const s = JSON.parse(localStorage.getItem('doctorShiftSession') || 'null');
            if (s && (s.availability_status || s.status)) {
              statusFound = s.availability_status || s.status;
            }
          } catch (e) {
            /* ignore */
          }
        }

        // 5. Check direct localStorage keys updated by dashboard/slider
        if (!statusFound) {
          const direct = localStorage.getItem('doctor_status') || localStorage.getItem('availability_status');
          if (direct) {
            statusFound = direct;
          }
        }

        // 6. Check counter table ONLY if dedicated and if not conflicting with an active serving token
        if (!statusFound && token) {
          try {
            const cRes = await axios.get(`${baseUrl}/v1/admin/counter`, { headers });
            const counters = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.data || []);
            if (Array.isArray(counters) && counters.length > 0) {
              const matched = counters.find(c => {
                if (userId && c.user_id && String(c.user_id) === String(userId)) return true;
                if (c.attendant_name && userName) {
                  const att = c.attendant_name.toLowerCase().replace(/^dr\.?\s*/i, '').trim();
                  if (att.includes(userName) || userName.includes(att)) return true;
                }
                return false;
              });
              if (matched && (matched.availability_status || matched.doctor_status)) {
                const cStatus = matched.availability_status || matched.doctor_status;
                const activeTok = matched.current_token || localStorage.getItem('current_token');
                // Guard: Never set to Busy from counter table if there is no active token attended
                if (String(cStatus).toLowerCase().includes('busy') && (!activeTok || activeTok === '--')) {
                  // Do not force Busy
                } else {
                  statusFound = cStatus;
                }
              }
            }
          } catch (cErr) {
            /* ignore */
          }
        }

        // 7. Check my-shift endpoint
        if (!statusFound && token) {
          try {
            let res = await axios.get(`${baseUrl}/v1/admin/doctor/my-shift`, { headers });
            if (res?.data?.data?.availability_status || res?.data?.data?.status) {
              const sStatus = res.data.data.availability_status || res.data.data.status;
              const activeTok = res.data.data.current_token || localStorage.getItem('current_token');
              if (String(sStatus).toLowerCase().includes('busy') && (!activeTok || activeTok === '--')) {
                // Do not force Busy without active token
              } else {
                statusFound = sStatus;
              }
            }
          } catch (mErr) {
            /* ignore */
          }
        }

        if (statusFound) {
          const rawTok = localStorage.getItem('current_token') || '';
          let isTokenFinished = false;
          if (rawTok && rawTok !== '--' && rawTok !== 'null') {
            try {
              const compIds = JSON.parse(localStorage.getItem('doctor_completed_token_ids') || '[]');
              const compCache = JSON.parse(localStorage.getItem('doctor_completed_tokens_cache') || '[]');
              isTokenFinished = compIds.includes(String(rawTok)) || compCache.some(t => String(t.token_number || t.token || t.id) === String(rawTok));
              if (isTokenFinished) {
                localStorage.setItem('current_token', '--');
              }
            } catch (e) {
              /* ignore */
            }
          }
          const activeTok = isTokenFinished ? '--' : rawTok;
          const hasActiveToken = activeTok && activeTok !== '--' && activeTok !== 'null';
          const localSt = localStorage.getItem('doctor_status') || localStorage.getItem('availability_status');

          let resolvedStatus = statusFound;
          // Guard: If doctor set Available, or backend reports Busy but there is no active token in consultation, respect Available
          if (String(statusFound).toLowerCase().includes('busy')) {
            if (!hasActiveToken || (localSt && localSt.toLowerCase().includes('available'))) {
              resolvedStatus = 'Available';
            }
          } else if (localSt && localSt.toLowerCase().includes('available')) {
            resolvedStatus = 'Available';
          }
          const normalized = this.normalizeStatus(resolvedStatus);
          if (normalized !== this.doctorStatus) {
            this.doctorStatus = normalized;
            localStorage.setItem('doctor_status', normalized);
            localStorage.setItem('availability_status', normalized);
          }
        }
      } catch (e) {
        console.warn("Could not fetch doctor shift status:", e);
      }
    },

    async changeDoctorStatus(newStatus) {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('admin_token');
        let user = {};
        try {
          user = JSON.parse(localStorage.getItem('user') || '{}');
        } catch (e) {
          user = {};
          console.warn("Could not parse user in Navbar:", e);
        }
        const counterId = this.currentCounterId || user.counter_id || localStorage.getItem('counter_id') || null;
        const statusToSet = typeof newStatus === 'string' ? newStatus : this.doctorStatus;
        this.doctorStatus = statusToSet;
        localStorage.setItem('availability_status', statusToSet);
        localStorage.setItem('doctor_status', statusToSet);
        window.dispatchEvent(new CustomEvent('doctor-status-synced', { detail: { status: statusToSet } }));

        const baseUrl = (process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_API_URL || '/api').replace(/\/+$/, '');
        let savedShift = {};
        try {
          savedShift = JSON.parse(localStorage.getItem('doctorShiftSession') || '{}');
        } catch (e) {
          savedShift = {};
        }

        const payload = {
          start_time: savedShift.start_time || '09:00',
          end_time: savedShift.end_time || '17:00',
          slot_duration_minutes: Number(savedShift.slot_duration_minutes) || 30,
          shift: savedShift.shift || `${savedShift.start_time || '09:00'} - ${savedShift.end_time || '17:00'}`,
          availability_status: statusToSet,
          status: statusToSet,
          user_id: user.id || localStorage.getItem('id'),
          doctor_id: user.id || localStorage.getItem('id'),
          counter_id: counterId,
          doctor_name: localStorage.getItem('userName') || user.name || ''
        };
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // 1. Primary: Save to counters table via set-timing
        try {
          await axios.post(`${baseUrl}/v1/admin/doctor/set-timing`, payload, { headers });
        } catch (timingErr) {
          console.warn("Doctor set-timing sync fallback:", timingErr.message);
        }

        // 2. Direct counter table update (Admin only; doctors use set-timing above)
        if (counterId && !this.isDoctor) {
          try {
            await axios.put(`${baseUrl}/v1/admin/counter/update/${counterId}`, {
              availability_status: statusToSet,
              status: statusToSet === 'Available' ? 1 : 0
            }, { headers });
          } catch (cErr) {
            console.warn("Direct counter update skipped:", cErr.message);
          }
        }

        // 3. Direct user table update
        if (payload.user_id) {
          try {
            await axios.post(`${baseUrl}/v1/admin/user/update/${payload.user_id}`, {
              availability_status: statusToSet,
              doctor_status: statusToSet,
              status: 1
            }, { headers });
          } catch (uErr) {
            console.warn("User update sync skipped:", uErr.message);
          }
        }

        // 4. Supplementary doctor status call
        try {
          if (this.$http && typeof this.$http.post === 'function') {
            await this.$http.post('/api/v1/admin/doctor/status', payload);
          } else {
            await axios.post(`${baseUrl}/v1/admin/doctor/status`, payload, { headers });
          }
        } catch (apiErr) {
          console.warn("Doctor status endpoint skipped:", apiErr.message);
        }

        user.availability_status = statusToSet;
        try {
          localStorage.setItem('user', JSON.stringify(user));
        } catch (e) {
          console.warn("Could not persist user JSON:", e);
        }
        localStorage.setItem('availability_status', statusToSet);
        localStorage.setItem('doctor_status', statusToSet);

        // Sync with doctorShiftSession
        try {
          const sessionStr = localStorage.getItem('doctorShiftSession');
          if (sessionStr) {
            const s = JSON.parse(sessionStr);
            s.status = statusToSet;
            s.availability_status = statusToSet;
            localStorage.setItem('doctorShiftSession', JSON.stringify(s));
          }
        } catch (e) {
          console.warn("Could not sync doctorShiftSession status:", e);
        }

        // Sync with hospitalDoctorShifts
        try {
          const allShifts = JSON.parse(localStorage.getItem('hospitalDoctorShifts') || '{}');
          const myId = String(localStorage.getItem('id') || '');
          const myEmail = String(localStorage.getItem('adminEmail') || localStorage.getItem('email') || '').toLowerCase().trim();
          const myName = (localStorage.getItem('userName') || '').toLowerCase().replace(/^dr\.?\s*/i, '').trim();

          if (myId && allShifts[myId]) {
            allShifts[myId].status = statusToSet;
            allShifts[myId].availability_status = statusToSet;
          }
          if (myEmail && allShifts[myEmail]) {
            allShifts[myEmail].status = statusToSet;
            allShifts[myEmail].availability_status = statusToSet;
          }
          if (myName && allShifts[myName]) {
            allShifts[myName].status = statusToSet;
            allShifts[myName].availability_status = statusToSet;
          }
          localStorage.setItem('hospitalDoctorShifts', JSON.stringify(allShifts));
        } catch (e) {
          console.warn("Could not sync hospitalDoctorShifts:", e);
        }

        window.dispatchEvent(new CustomEvent('doctor-status-synced', { detail: { status: statusToSet } }));
        window.dispatchEvent(new CustomEvent('doctor-availability-changed', { detail: payload }));

        if (this.$toast && typeof this.$toast.success === 'function') {
          this.$toast.success(`Status updated to ${statusToSet}`);
        } else if (typeof Swal !== 'undefined' && Swal.fire) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: `Status: ${statusToSet}`,
            showConfirmButton: false,
            timer: 2000
          });
        }
      } catch (err) {
        console.error("Failed to update status:", err);
        if (this.$toast && typeof this.$toast.error === 'function') this.$toast.error('Failed to update status');
      }
    },

    handleDoctorStatusSynced(e) {
      if (!e || !e.detail) return;
      const myId = String(localStorage.getItem('id') || '');
      const myEmail = String(localStorage.getItem('adminEmail') || localStorage.getItem('email') || '').toLowerCase().trim();
      const myName = (localStorage.getItem('userName') || '').toLowerCase().replace(/^dr\.?\s*/i, '').trim();

      const dId = String(e.detail.doctor_id || e.detail.user_id || '').trim();
      const dEmail = String(e.detail.doctor_email || e.detail.email || '').toLowerCase().trim();
      const dName = String(e.detail.doctor_name || e.detail.name || '').toLowerCase().replace(/^dr\.?\s*/i, '').trim();

      // If payload specifies a doctor, verify it matches me
      const isForMe = (!dId && !dEmail && !dName) ? true : (
        (dId && myId && dId === myId) ||
        (dEmail && myEmail && dEmail === myEmail) ||
        (dName && myName && (dName.includes(myName) || myName.includes(dName)))
      );

      const rawStatus = e.detail.status || e.detail.availability_status;
      if (isForMe && rawStatus) {
        const normalized = this.normalizeStatus(rawStatus);
        this.doctorStatus = normalized;
        localStorage.setItem('doctor_status', normalized);
        localStorage.setItem('availability_status', normalized);
      }
    },

    handleDoctorAvailabilityChanged(e) {
      if (!this.isDoctor) return;
      this.handleDoctorStatusSynced(e);
      this.fetchDoctorStatus();
    },

    handleStorageChange(e) {
      if (!this.isDoctor) return;
      if (
        !e ||
        e.key === 'hospitalDoctorShifts' ||
        e.key === 'doctorAvailabilityLastUpdated' ||
        e.key === 'doctor_status' ||
        e.key === 'availability_status' ||
        (e.key && e.key.startsWith('doctorShift_'))
      ) {
        this.fetchDoctorStatus();
      }
    },

    handleClickOutside(e) {
      if (!document.getElementById('lang-area')?.contains(e.target))  this.showLangDropdown  = false;
      if (!document.getElementById('search-area')?.contains(e.target)) this.showSuggestions  = false;
      if (!document.getElementById('user-area')?.contains(e.target))  this.showUserDropdown  = false;
    },

    async fetchTenantStorage() {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('admin_token');
        if (!token) return;
        const apiBase = (process.env.VUE_APP_API_BASE_URL || '/api').replace(/\/+$/, '');

        const uStr = localStorage.getItem('user');
        let userObj = {};
        try {
          userObj = JSON.parse(uStr || '{}');
        } catch (e) {
          userObj = {};
        }
        const isSuper = userObj.type === 1 || String(userObj.role || userObj.role_name || localStorage.getItem('userRole') || '').toLowerCase().includes('superadmin');
        const tId = userObj.tenant_id || userObj.tenantId || localStorage.getItem('tenant_id') || localStorage.getItem('tenantId');
        const cleanTId = tId && tId !== 'null' && tId !== 'undefined' ? tId : null;

        const qParam = cleanTId ? `?tenant_id=${cleanTId}` : '';
        // Exact routes from Express router:
        // router.get('/tenant/storage-usage', middleware, StorageController.getTenantStorageUsage);
        // router.get('/tenants/storage-usage', middleware, StorageController.getTenantStorageUsage);
        const candidates = isSuper
          ? [
              `${apiBase}/v1/admin/tenants/storage-usage${qParam}`,
              `${apiBase}/v1/admin/tenant/storage-usage${qParam}`,
            ]
          : [
              `${apiBase}/v1/admin/tenant/storage-usage${qParam}`,
              `${apiBase}/v1/admin/tenants/storage-usage${qParam}`,
            ];

        let foundData = null;
        for (const url of candidates) {
          try {
            const res = await axios.get(url, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 4000
            });
            if (res.data) {
              const storage = res.data.storage || res.data.data?.storage || res.data.data;
              const sysStorage = res.data.system_storage;
              if (storage && (storage.total_used_mb !== undefined || storage.quota_mb !== undefined || storage.percentage_used !== undefined)) {
                foundData = storage;
                console.log(`[Navbar] Storage usage loaded from ${url}:`, storage);
                break;
              } else if (sysStorage) {
                foundData = {
                  total_used_gb: sysStorage.total_used_gb,
                  total_used_mb: (sysStorage.total_used_gb * 1024),
                  quota_gb: sysStorage.total_allocated_gb,
                  quota_mb: (sysStorage.total_allocated_gb * 1024),
                  percentage_used: sysStorage.overall_usage_pct
                };
                console.log(`[Navbar] System storage summary loaded from ${url}:`, foundData);
                break;
              } else if (res.data.status && res.data.storage) {
                foundData = res.data.storage;
                break;
              } else if (res.data.success && res.data.storage) {
                foundData = res.data.storage;
                break;
              }
            }
          } catch (err) {
            // Check next candidate URL
          }
        }

        if (foundData) {
          const quota = Number(foundData.quota_mb || 5120);
          const usedMb = Number(foundData.total_used_mb || 0);
          const usedGb = foundData.total_used_gb !== undefined ? foundData.total_used_gb : (usedMb / 1024).toFixed(2);
          const pct = foundData.percentage_used !== undefined ? Number(foundData.percentage_used) : (quota > 0 ? ((usedMb / quota) * 100).toFixed(1) : 0);

          this.tenantStorage = {
            total_used_mb: usedMb,
            total_used_gb: usedGb,
            quota_mb: quota,
            percentage_used: Number(pct)
          };
        }
      } catch (e) {
        console.warn("[Navbar] fetchTenantStorage error:", e.message);
      }
    }
  },
  mounted() {
    this.fetchTenantStorage();
    // UPDATED: Ensure theme is applied immediately on mount for all roles
    if (!localStorage.getItem('themeColor')) {
      localStorage.setItem('themeColor', this.activeTheme);
    }
    
    document.addEventListener("click", this.handleClickOutside);
    this.applyThemeStyles(this.activeTheme);
    
    this.adminName  = localStorage.getItem('userName')  || 'Admin';
    this.adminEmail = localStorage.getItem('adminEmail') || 'admin@bhoomida.com';
    this.adminRole  = localStorage.getItem('userRole')  || 'User'; 

    if (this.isDoctor) {
      this.fetchDoctorStatus();
      if (!this.doctorStatusPoll) {
        this.doctorStatusPoll = setInterval(() => this.fetchDoctorStatus(), 5000);
      }
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('admin_token');
        const rawBase = process.env.VUE_APP_API_BASE_URL || window.location.origin;
        const parsedUrl = new URL(rawBase.startsWith('http') ? rawBase : window.location.origin);
        const socketUrl = process.env.VUE_APP_SOCKET_URL || `${parsedUrl.protocol}//${parsedUrl.host}`;
        this.socket = io(socketUrl, {
          auth: { token },
          transports: ['websocket', 'polling'],
          reconnection: true
        });
        this.socket.on('doctor_status_updated', (payload) => {
          if (payload) this.handleDoctorStatusSynced({ detail: payload });
        });
        this.socket.on('doctor_availability_changed', (payload) => {
          if (payload) this.handleDoctorStatusSynced({ detail: payload });
        });
      } catch (sockErr) {
        console.warn("Navbar socket init note:", sockErr);
      }
    }
    window.addEventListener("doctor-status-synced", this.handleDoctorStatusSynced);
    window.addEventListener("doctor-availability-changed", this.handleDoctorAvailabilityChanged);
    window.addEventListener("storage", this.handleStorageChange);

    // REDIRECTION LOGIC: If on Dashboard but shouldn't be, redirect to Branches
    if (this.$route.name === 'Dashboard' && !this.hasAccess('Dashboard')) {
       if (this.hasAccess('Branches')) {
         this.$router.push({ name: 'Branches' });
       }
    }
  },
  unmounted() {
    if (this.doctorStatusPoll) {
      clearInterval(this.doctorStatusPoll);
      this.doctorStatusPoll = null;
    }
    if (this.socket) {
      try {
        this.socket.disconnect();
      } catch (e) {
        /* ignore */
      }
    }
    window.removeEventListener("doctor-status-synced", this.handleDoctorStatusSynced);
    window.removeEventListener("doctor-availability-changed", this.handleDoctorAvailabilityChanged);
    window.removeEventListener("storage", this.handleStorageChange);
    document.removeEventListener("click", this.handleClickOutside);
  }
};
</script>

<style scoped>
/* ── NAVBAR ── */
.custom-navbar {
  border: none;
  border-radius: 12px !important;
  transition: background-color 0.3s ease;
}

/* ── SEARCH BAR RESPONSIVENESS ── */
.search-container {
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  width: 100%; /* Flexible by default */
  max-width: 240px;
  transition: all 0.25s ease;
}

/* On Desktop, handle expansion */
@media (min-width: 768px) {
  .search-container.search-focused { 
    max-width: 320px; 
  }
}

/* On Mobile, take full available width but allow for other icons */
@media (max-width: 576px) {
  .search-container {
    max-width: 180px;
  }
  .search-container.search-focused {
    max-width: 200px;
  }
}

.custom-input-no-focus:focus {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
}

/* ── USER PILL ── */
.user-pill {
  padding: 5px 8px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
}

.user-pill:hover {
  background: rgba(255, 255, 255, 0.1);
}
.user-avatar-small {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── DROPDOWN MENU ── */
.custom-dropdown-menu {
  position: absolute;
  top: 115%;
  right: 0;
  z-index: 1050;
  min-width: 220px;
  padding: 6px;
  background-color: #1a2535;
  border-radius: 12px;
  list-style: none;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
}

/* Fix for mobile dropdown positioning */
@media (max-width: 576px) {
  .custom-dropdown-menu {
    right: -10px;
    min-width: 200px;
  }
}

.dropdown-item-custom {
  padding: 9px 13px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.78) !important;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
}
.dropdown-item-custom:hover {
  background-color: rgba(255, 255, 255, 0.07);
  color: #ffffff !important;
}

/* ── THEME SECTION IN DROPDOWN ── */
.theme-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.theme-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255, 255, 255, 0.45);
  margin: 0;
}
.theme-swatch {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  box-sizing: border-box;
  transition: transform 0.15s ease;
}
.theme-swatch:hover {
  transform: scale(1.2);
}

/* ── LOGOUT BUTTON ── */
.logout-btn {
  padding: 9px 13px;
  font-size: 13px;
  font-weight: 600;
  color: #fc8181 !important;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  transition: all 0.18s ease;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  margin-top: 4px;
  padding-top: 10px;
}
.logout-btn:hover {
  background-color: rgba(252, 129, 129, 0.1);
  color: #ffffff !important;
}
.logout-btn i {
  color: #fc8181 !important;
}
.logout-btn:hover i {
  color: #ffffff !important;
}

/* ── DOCTOR STATUS BADGE ── */
.doctor-status-badge {
  display: flex;
  align-items: center;
}
.status-dropdown {
  font-weight: 600;
  border-radius: 20px;
  padding: 4px 12px;
  cursor: pointer;
  font-size: 12px;
}
.status-available { background-color: #e8f5e9; color: #2e7d32; border: 1px solid #81c784; }
.status-busy      { background-color: #ffebee; color: #c62828; border: 1px solid #e57373; }
.status-break     { background-color: #fff8e1; color: #f57f17; border: 1px solid #ffd54f; }
.status-offline   { background-color: #eceff1; color: #455a64; border: 1px solid #b0bec5; }
</style>